// 지역별 환경 키트·텍스처 해석(c023, three 없음 — 단위 테스트용 순수 함수).
// 지역3 서리 묘지·지역4 가라앉은 사원(c023)·지역5 황금 모래 폐도·지역6 심연의 성채(3개월 C2)는 같은 KitKind 이름의 지역판(public/assets/<지역>/kit_<종류>.glb)을 먼저 쓰고,
// 지역판이 없거나 읽지 못하면 지역1 이끼 유적 키트(r1/kit_<종류>)로 대신한다. 배치 규칙(layout.ts)은 그대로 재사용한다.
import type { MapId } from '../../shared/levels';
import type { KitKind } from './layout';

/** 지역판 키트가 있는 지역 */
export type KitRegion = 'r3' | 'r4' | 'r5' | 'r6';

/** 지역판으로 만든 종류(tools/blender/r34_env_kits.py·r56_env_kits.py KINDS와 같다). 계단·마을 소품은 r1/마을 그대로 */
export const REGION_KIT_KINDS: ReadonlySet<KitKind> = new Set<KitKind>([
  'wall', 'wall_broken', 'pillar', 'pillar_broken', 'arch', 'statue', 'rock', 'rock_lo', 'tree', 'tree_lo', 'mushrooms', 'rubble', 'banner', 'brazier', 'crates',
]);

/**
 * 맵의 키트 지역. legacy(?envlegacy, 전후 비교용)면 지역판을 쓰지 않는다(c021까지의 화면: r1 키트 + 색 보정).
 */
export function kitRegionOf(mapId: MapId, legacy = false): KitRegion | null {
  if (legacy) return null;
  return mapId === 'r3' || mapId === 'r4' || mapId === 'r5' || mapId === 'r6' ? mapId : null;
}

/** 읽기 시도 순서(확장자 없음): 지역판 → 기본 파일. 지역판이 없는 종류·지역이면 기본 파일 하나 */
export function kitCandidates(kind: KitKind, base: string, region: KitRegion | null): string[] {
  if (!region || !REGION_KIT_KINDS.has(kind)) return [base];
  return [region + '/kit_' + kind, base];
}

/** 캐시 키: 같은 종류라도 지역판과 r1판은 다른 자산이다 */
export function kitCacheKey(kind: KitKind, region: KitRegion | null): string {
  return (region && REGION_KIT_KINDS.has(kind) ? region : 'base') + ':' + kind;
}

/** 지역 바닥·풀밭·원경 텍스처(public/assets 상대 경로). null이면 기존 r1·마을 텍스처 */
export interface RegionTextures {
  readonly floor: string;
  readonly ground: string;
  readonly pano: string;
}
export const REGION_TEXTURES: Readonly<Record<KitRegion, RegionTextures>> = {
  r3: { floor: 'r3/tex/floor_snow_stone.webp', ground: 'r3/tex/ground_snow.webp', pano: 'r3/tex/bg_r3_panorama.webp' },
  r4: { floor: 'r4/tex/floor_wet_stone.webp', ground: 'r4/tex/ground_swamp.webp', pano: 'r4/tex/bg_r4_panorama.webp' },
  r5: { floor: 'r5/tex/floor_sandstone.webp', ground: 'r5/tex/ground_sand.webp', pano: 'r5/tex/bg_r5_panorama.webp' },
  r6: { floor: 'r6/tex/floor_obsidian.webp', ground: 'r6/tex/ground_void_rock.webp', pano: 'r6/tex/bg_r6_panorama.webp' },
};

/** 쿼리 문자열에 envlegacy가 있으면 지역판을 끈다(전후 캡처 픽스처) */
export function legacyFromSearch(search: string | undefined | null): boolean {
  if (!search) return false;
  return new URLSearchParams(search).has('envlegacy');
}

/** 대기 입자(c023·C2): r3 눈송이, r4 포자, r5 흩날리는 모래 먼지, r6 위로 떠오르는 보라 불씨. 품질 high 기준 입자 수 상한(밀도 배율을 곱한다). InstancedMesh 1개 = 드로우콜 +1 */
export type DriftKind = 'snow' | 'spore' | 'dust' | 'ember';
export const DRIFT_BUDGET: Readonly<Record<DriftKind, number>> = { snow: 240, spore: 120, dust: 200, ember: 140 };
/** 지역의 대기 입자 종류 */
export const REGION_DRIFT: Readonly<Record<KitRegion, DriftKind>> = { r3: 'snow', r4: 'spore', r5: 'dust', r6: 'ember' };
export function driftCount(kind: DriftKind, density: number): number {
  return Math.max(0, Math.min(DRIFT_BUDGET[kind], Math.round(DRIFT_BUDGET[kind] * density)));
}
